import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SETTINGS_ROLES } from "@/lib/nav";

const Stat = ({ label, value, icon, tone }) => {
  const Ic = Icons[icon] || Icons.Circle;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid={`sched-stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${tone}`}><Ic className="h-4 w-4" /></span>
        <span className="text-2xl font-bold text-slate-900">{value}</span>
      </div>
      <p className="text-xs text-slate-500 mt-2">{label}</p>
    </div>
  );
};

const ApptRow = ({ a }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0" data-testid={`sched-appt-${a.id}`}>
    <span className="w-1.5 h-9 rounded-full shrink-0" style={{ backgroundColor: a.color || "#1f6feb" }} />
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium text-slate-800 truncate">{a.meeting_type_name} · {a.contact_name || "Client"}</div>
      <div className="text-xs text-slate-400">{a.date} · {a.start_time} ({a.timezone}) · {a.duration_min}m</div>
    </div>
    {(a.zoom || {}).join_url && <a href={a.zoom.join_url} target="_blank" rel="noreferrer" className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1"><Icons.Video className="h-3.5 w-3.5" />Zoom</a>}
    <span className={`text-[11px] px-2 py-0.5 rounded-full ${a.status === "confirmed" ? "bg-emerald-50 text-emerald-700" : a.status === "requested" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{a.status}</span>
  </div>
);

export default function SchedulingDashboard() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [adminOpen, setAdminOpen] = useState(false);
  const isSettingsRole = SETTINGS_ROLES.includes(user?.role);

  useEffect(() => { api.get("/scheduling/dashboard").then((r) => setD(r.data)).catch(() => setD({})); }, []);
  if (!d) return <div className="p-10 text-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500 inline" /></div>;
  const s = d.stats || {};
  const Panel = ({ title, items, empty, icon }) => {
    const Ic = Icons[icon] || Icons.Calendar;
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-2"><Ic className="h-4 w-4 text-slate-400" /><h3 className="font-heading font-semibold text-slate-800">{title}</h3><span className="text-xs text-slate-400">({(items || []).length})</span></div>
        {(items || []).length === 0 ? <p className="text-sm text-slate-400 py-3">{empty}</p> : <div>{items.slice(0, 6).map((a) => <ApptRow key={a.id} a={a} />)}</div>}
      </div>
    );
  };
  return (
    <div className="p-6 max-w-[1280px] mx-auto space-y-5" data-testid="scheduling-dashboard">
      {/* Workspace tab bar */}
      <div className="flex items-center gap-0.5 border-b border-slate-200 pb-0 -mb-1" data-testid="scheduling-workspace-tabs">
        <button data-testid="ws-tab-calendar" onClick={() => navigate("/scheduling/calendar")}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors">
          <Icons.Calendar className="h-4 w-4" />Calendar
        </button>
        <button data-testid="ws-tab-appointments" onClick={() => navigate("/scheduling/appointments")}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors">
          <Icons.CalendarCheck className="h-4 w-4" />Appointments
        </button>
        <div className="relative ml-auto">
          <button data-testid="ws-tab-admin" onClick={() => setAdminOpen((v) => !v)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${adminOpen ? "border-slate-700 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"}`}>
            <Icons.Settings2 className="h-4 w-4" />Administration
            <Icons.ChevronDown className={`h-3.5 w-3.5 transition-transform ${adminOpen ? "rotate-180" : ""}`} />
          </button>
          {adminOpen && (
            <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-lg z-20 p-2" data-testid="scheduling-admin-panel">
              <button data-testid="sched-admin-link-meeting-types" onClick={() => { setAdminOpen(false); navigate("/scheduling/meeting-types"); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                <Icons.BookMarked className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="text-left"><div className="font-medium">Meeting Type Library</div><div className="text-xs text-slate-400">View and configure meeting types</div></div>
              </button>
              <button data-testid="sched-admin-link-availability" onClick={() => { setAdminOpen(false); navigate("/scheduling/availability"); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                <Icons.Clock className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="text-left"><div className="font-medium">Staff Availability</div><div className="text-xs text-slate-400">Manage your availability</div></div>
              </button>
              {isSettingsRole && (
                <button data-testid="sched-admin-link-settings" onClick={() => { setAdminOpen(false); navigate("/scheduling/settings"); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                  <Icons.Settings2 className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="text-left"><div className="font-medium">Scheduling Settings</div><div className="text-xs text-slate-400">Defaults, integrations &amp; providers</div></div>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-slate-900">Scheduling Dashboard</h1><p className="text-sm text-slate-500">The Appointment Engine at a glance — what's happening today and next.</p></div>
        <Link to="/scheduling/appointments" data-testid="sched-book-cta" className="inline-flex items-center gap-2 bg-[#171B2E] text-white text-sm rounded-lg px-4 py-2.5"><Icons.CalendarPlus className="h-4 w-4 text-[#F5A623]" />Book Appointment</Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Today" value={s.today ?? 0} icon="CalendarDays" tone="bg-blue-50 text-blue-600" />
        <Stat label="This Week" value={s.this_week ?? 0} icon="CalendarRange" tone="bg-indigo-50 text-indigo-600" />
        <Stat label="Awaiting Confirm" value={s.awaiting_confirmation ?? 0} icon="Clock" tone="bg-amber-50 text-amber-600" />
        <Stat label="Zoom Today" value={s.zoom_today ?? 0} icon="Video" tone="bg-sky-50 text-sky-600" />
        <Stat label="Cancelled" value={s.cancelled ?? 0} icon="CalendarX" tone="bg-rose-50 text-rose-600" />
        <Stat label="No-Shows" value={s.no_shows ?? 0} icon="UserX" tone="bg-slate-100 text-slate-500" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Today's Appointments" items={d.today} empty="Nothing scheduled today." icon="Sun" />
        <Panel title="Upcoming (7 days)" items={d.upcoming} empty="No upcoming appointments." icon="CalendarClock" />
        <Panel title="Waiting for Confirmation" items={d.awaiting_confirmation} empty="All confirmed." icon="Hourglass" />
        <Panel title="Recently Completed" items={d.recently_completed} empty="No completed meetings yet." icon="CheckCircle2" />
      </div>
    </div>
  );
}
